/**
 * @file 番型计算与计分（Scoring）。
 *
 * 核心主张（见 `design/gdd/04-scoring.md` §1.2）：**加法番数模型**——
 * `score = baseScore × 2^min(Σfan, fanCap)`，复合番型由基础番型相加自然得出，
 * 绝不为「清对 / 龙七对 / 十八罗汉」等写特判（D-05）。
 *
 * 本文件纯函数、平台无关，只依赖 `win.ts` / `tiles.ts` 与 `config.ts` 的类型。
 */

import type { Meld, WinKind, Suit } from './types';
import type { RuleConfig } from './config';
import { keyOfKind, suitToIndex, rankOfKind } from './tiles';
import { checkStandard, isSevenPairs, hasAllTripletsDecomp } from './win';
import { M1_ENFORCED_FAN_CAP } from './config';

/** 胡牌上下文（影响情境番型）。 */
export interface WinContext {
  readonly winType: 'zimo' | 'dianpao' | 'qiang_gang';
  /** 杠上花：赢家刚杠后补摸自摸。 */
  readonly gangFlower: boolean;
  /** 杠上炮：放炮者刚杠后打出被胡。 */
  readonly discarderGangFlower: boolean;
  /** 海底捞月：自摸且摸到最后一张。 */
  readonly haiDi: boolean;
  /** 天胡（默认关闭）。 */
  readonly tianHu: boolean;
  /** 地胡（默认关闭）。 */
  readonly diHu: boolean;
}

/** 番型计算结果。 */
export interface FanResult {
  readonly fanTags: readonly string[];
  readonly rawFan: number;
}

/** 由公开 `winKind` 与 `isLastTile` 推导上下文（天胡/地胡默认 false，由引擎另行置位）。 */
export function deriveContext(winKind: WinKind, isLastTile: boolean): WinContext {
  let winType: WinContext['winType'] = 'zimo';
  let gangFlower = false;
  let discarderGangFlower = false;
  if (winKind === 'zimo') winType = 'zimo';
  else if (winKind === 'gang_shang_hua') {
    winType = 'zimo';
    gangFlower = true;
  } else if (winKind === 'dianpao') winType = 'dianpao';
  else if (winKind === 'gang_shang_pao') {
    winType = 'dianpao';
    discarderGangFlower = true;
  } else if (winKind === 'qiang_gang') winType = 'qiang_gang';
  return {
    winType,
    gangFlower,
    discarderGangFlower,
    haiDi: winType === 'zimo' && isLastTile,
    tianHu: false,
    diHu: false,
  };
}

/**
 * 「根」：某一牌种持有满 4 张记 1 根。必须**统一按牌种统计总张数**
 * （手牌 + 副露按实体张数），否则会漏计「碰 3 张 + 手留 1 张」这类常见场景。
 */
export function countGen(handCounts: Uint8Array, melds: readonly Meld[]): number {
  const total = new Uint8Array(27);
  for (let k = 0; k < 27; k++) total[k] = handCounts[k] ?? 0;
  for (const m of melds) {
    const k = keyOfKind(m.kind);
    total[k] = (total[k] ?? 0) + (m.type === 'peng' ? 3 : 4);
  }
  let gen = 0;
  for (let k = 0; k < 27; k++) gen += Math.floor((total[k] ?? 0) / 4);
  return gen;
}

/** 清一色：手牌 + 全部副露的花色集合 size === 1。 */
export function isFlush(counts: Uint8Array, melds: readonly Meld[]): boolean {
  let mask = 0;
  for (let k = 0; k < 27; k++) if ((counts[k] ?? 0) > 0) mask |= 1 << Math.floor(k / 9);
  for (const m of melds) {
    mask |= 1 << suitToIndex(m.kind.slice(0, -1) as Suit);
  }
  let bits = 0;
  for (let b = 0; b < 3; b++) if (mask & (1 << b)) bits++;
  return bits === 1;
}

/** 将对：全部由刻子构成（对对胡）且每张牌点数为 2/5/8。 */
export function allJiangDui(counts: Uint8Array, melds: readonly Meld[]): boolean {
  for (let k = 0; k < 27; k++) {
    if ((counts[k] ?? 0) > 0) {
      const r = (k % 9) + 1;
      if (r !== 2 && r !== 5 && r !== 8) return false;
    }
  }
  for (const m of melds) {
    const r = rankOfKind(m.kind);
    if (r !== 2 && r !== 5 && r !== 8) return false;
  }
  return true;
}

function sumCountsLocal(counts: Uint8Array): number {
  let t = 0;
  for (let k = 0; k < 27; k++) t += counts[k] ?? 0;
  return t;
}

function applyContextTags(tags: string[], fan: number, config: RuleConfig, ctx: WinContext): { tags: string[]; fan: number } {
  const f = config.fan;
  if (ctx.winType === 'zimo' && ctx.gangFlower && f.GANG_SHANG_HUA.enabled) {
    tags.push('GANG_SHANG_HUA');
    fan += f.GANG_SHANG_HUA.fan;
  }
  if (ctx.winType === 'dianpao' && ctx.discarderGangFlower && f.GANG_SHANG_PAO.enabled) {
    tags.push('GANG_SHANG_PAO');
    fan += f.GANG_SHANG_PAO.fan;
  }
  if (ctx.winType === 'qiang_gang' && f.QIANG_GANG_HU.enabled) {
    tags.push('QIANG_GANG_HU');
    fan += f.QIANG_GANG_HU.fan;
  }
  if (ctx.winType === 'zimo' && ctx.haiDi && f.HAI_DI_LAO_YUE.enabled) {
    tags.push('HAI_DI_LAO_YUE');
    fan += f.HAI_DI_LAO_YUE.fan;
  }
  if (f.TIAN_HU.enabled && ctx.tianHu) {
    tags.push('TIAN_HU');
    fan += f.TIAN_HU.fan;
  }
  if (f.DI_HU.enabled && ctx.diHu) {
    tags.push('DI_HU');
    fan += f.DI_HU.fan;
  }
  return { tags, fan };
}

function scoreStandard(
  counts: Uint8Array,
  melds: readonly Meld[],
  config: RuleConfig,
  ctx: WinContext,
  meldCount: number,
): FanResult {
  const tags: string[] = [];
  let fan = 0;
  const f = config.fan;
  const gen = countGen(counts, melds);
  const allTriplets = hasAllTripletsDecomp(counts);
  const jinGouDiao = meldCount === 4 && sumCountsLocal(counts) === 2;
  const flush = isFlush(counts, melds);

  if (flush && f.QING_YI_SE.enabled) {
    tags.push('QING_YI_SE');
    fan += f.QING_YI_SE.fan;
  }
  if (allTriplets && f.DUI_DUI_HU.enabled) {
    tags.push('DUI_DUI_HU');
    fan += f.DUI_DUI_HU.fan;
  }
  if (gen > 0 && f.GEN.enabled) {
    tags.push('GEN');
    fan += f.GEN.fan * gen;
  }
  if (jinGouDiao && f.JIN_GOU_DIAO.enabled) {
    tags.push('JIN_GOU_DIAO');
    fan += f.JIN_GOU_DIAO.fan;
  }
  if (allTriplets && f.JIANG_DUI.enabled && allJiangDui(counts, melds)) {
    tags.push('JIANG_DUI');
    fan += f.JIANG_DUI.fan;
  }
  const c = applyContextTags(tags, fan, config, ctx);
  return { fanTags: c.tags, rawFan: c.fan };
}

function scoreSeven(counts: Uint8Array, melds: readonly Meld[], config: RuleConfig, ctx: WinContext): FanResult {
  const tags: string[] = [];
  let fan = 0;
  const f = config.fan;
  const gen = countGen(counts, melds);
  const flush = isFlush(counts, melds);

  if (f.QI_DUI.enabled) {
    tags.push('QI_DUI');
    fan += f.QI_DUI.fan;
  }
  if (flush && f.QING_YI_SE.enabled) {
    tags.push('QING_YI_SE');
    fan += f.QING_YI_SE.fan;
  }
  if (gen > 0 && f.GEN.enabled) {
    tags.push('GEN');
    fan += f.GEN.fan * gen;
  }
  const c = applyContextTags(tags, fan, config, ctx);
  return { fanTags: c.tags, rawFan: c.fan };
}

/**
 * 计算一手胡牌的番型：分别走「标准型」与「七对型」两条路径，取总番较大者（D-05 / 03 §4）。
 * 返回 `rawFan`（封顶前）；封顶由调用方按 `M1_ENFORCED_FAN_CAP` 处理。
 */
export function computeFan(
  counts: Uint8Array,
  melds: readonly Meld[],
  config: RuleConfig,
  ctx: WinContext,
  meldCount: number,
): FanResult {
  const total = sumCountsLocal(counts);
  const standardOk = checkStandard(counts, meldCount);
  const seven = meldCount === 0 && total === 14 && isSevenPairs(counts);

  const candidates: FanResult[] = [];
  if (standardOk) candidates.push(scoreStandard(counts, melds, config, ctx, meldCount));
  if (seven) candidates.push(scoreSeven(counts, melds, config, ctx));
  if (candidates.length === 0) return { fanTags: [], rawFan: 0 };

  let best = candidates[0]!;
  for (const c of candidates) if (c.rawFan > best.rawFan) best = c;
  return best;
}

/**
 * 查大叫用的「最大可能番数」：只计牌型番（对对胡/清一色/七对/根/金钩钓），
 * 不计情境番（杠上花/抢杠胡/海底等，因无真实胡牌事件）。见 `05` §6.3。
 */
export function maxPossibleFan(counts: Uint8Array, melds: readonly Meld[], config: RuleConfig, meldCount: number): number {
  const noCtx: WinContext = {
    winType: 'zimo',
    gangFlower: false,
    discarderGangFlower: false,
    haiDi: false,
    tianHu: false,
    diHu: false,
  };
  return computeFan(counts, melds, config, noCtx, meldCount).rawFan;
}

/** M1 锁定封顶番数（D-01）。 */
export const EFFECTIVE_FAN_CAP = M1_ENFORCED_FAN_CAP;
